/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import "./Modal.css";

import * as React from "react";
import { ReactNode, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { ReactComponent as CloseIcon } from "../images/icons/close.svg";
import { Typography20 } from "@slid/slid-ips";
import { useLocation } from "react-router-dom";

function PortalImpl({ onClose, children, title, closeOnClickOutside }: { children: ReactNode; closeOnClickOutside: boolean; onClose: () => void; title: string }) {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (modalRef.current !== null) {
      modalRef.current.focus();
    }
  }, []);

  useEffect(() => {
    let modalOverlayElement: HTMLElement | null = null;
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    const clickOutsideHandler = (event: MouseEvent) => {
      const target = event.target;
      if (modalRef.current !== null && !modalRef.current.contains(target as Node) && closeOnClickOutside) {
        onClose();
      }
    };
    const modelElement = modalRef.current;
    if (modelElement !== null) {
      modalOverlayElement = modelElement.parentElement;
      if (modalOverlayElement !== null) {
        modalOverlayElement.addEventListener("click", clickOutsideHandler);
      }
    }

    window.addEventListener("keydown", handler);

    return () => {
      window.removeEventListener("keydown", handler);
      if (modalOverlayElement !== null) {
        modalOverlayElement?.removeEventListener("click", clickOutsideHandler);
      }
    };
  }, [closeOnClickOutside, onClose]);

  return (
    <div className="Modal__overlay" role="dialog" onClick={onClose}>
      <div className="Modal__modal" tabIndex={-1} ref={modalRef} onClick={(e) => e.stopPropagation()}>
        <Typography20 text={title} color="--gray15" type={"h2"} weight={700} />
        <button className="Modal__closeButton" aria-label="Close modal" type="button" onClick={onClose}>
          <CloseIcon />
        </button>
        <div className="Modal__content">{children}</div>
      </div>
    </div>
  );
}

export default function Modal({ onClose, children, title, closeOnClickOutside = false }: { children: ReactNode; closeOnClickOutside?: boolean; onClose: () => void; title: string }): JSX.Element {
  // TODO: below code is for displaying editor modals on the node area in the video note. Since it's not the best place to having this logic here, we need to refactor this.
  const location = useLocation();
  const isVideoNote = location.pathname.split("/")[1] === "vdocs";
  const targetElement = isVideoNote ? (document.getElementById("editor-wrapper") as HTMLElement) : document.body;

  return createPortal(
    <PortalImpl onClose={onClose} title={title} closeOnClickOutside={closeOnClickOutside}>
      {children}
    </PortalImpl>,
    targetElement
  );
}
